import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, MessageCircle, Mail, Clock, MapPin, Globe, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_41f280ff-5c99-48b4-9414-474d5e72c2fc/artifacts/y9kopzrq_Black%20Elegant%20Modern%20Name%20Initials%20Monogram%20Logo.png";

// Services dropdown items
const servicesDropdown = [
  { name: 'Medical', path: '/services#medical' },
  { name: 'Dental', path: '/services#dental' },
  { name: 'Surgical', path: '/services#surgical' },
  { name: 'Insurance & Medicals', path: '/services#permits' },
];

// Price List dropdown items
const pricesDropdown = [
  { name: 'Medical', path: '/prices#medical-services' },
  { name: 'Dental', path: '/prices#dental-services' },
  { name: 'Surgical', path: '/prices#surgical-services' },
  { name: 'Insurance & Medicals', path: '/prices#insurance-medicals' },
  { name: 'Iron Infusions', path: '/prices#iron-infusions' },
  { name: 'Vivid IV Infusions', path: '/prices#vivid-wellness' },
];

// Packages dropdown items
const packagesDropdown = [
  { name: 'Antenatal Package', path: '/packages#antenatal' },
  { name: 'Diabetic Package', path: '/packages#diabetic' },
  { name: 'HIV Monitoring', path: '/packages#hiv' },
];

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'About', path: '/about' },
  { name: 'Services', path: '/services', hasDropdown: true, dropdown: servicesDropdown },
  { name: 'Price List', path: '/prices', hasDropdown: true, dropdown: pricesDropdown },
  { name: 'Packages', path: '/packages', hasDropdown: true, dropdown: packagesDropdown },
  { name: 'Weight Loss', path: '/weight-loss' },
  { name: 'Promotions', path: '/promotions' },
  { name: 'Contact Us', path: '/contact' },
];

// Language Switcher Component using Google Translate
const LanguageSwitcher = () => {
  const [isZulu, setIsZulu] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Check if already translated
    const checkCurrentLanguage = () => {
      const htmlLang = document.documentElement.lang;
      const hasZuluClass = document.body.classList.contains('translated-ltr');
      setIsZulu(htmlLang === 'zu' || hasZuluClass);
    };
    
    // Load Google Translate script
    if (!document.getElementById('google-translate-script')) {
      window.googleTranslateElementInit = function() {
        new window.google.translate.TranslateElement({
          pageLanguage: 'en',
          includedLanguages: 'en,zu',
          autoDisplay: false,
          multilanguagePage: true
        }, 'google_translate_element');
      };

      const script = document.createElement('script');
      script.id = 'google-translate-script';
      script.src = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
      script.async = true;
      document.body.appendChild(script);
    }

    checkCurrentLanguage();
    
    // Observer to detect translation changes
    const observer = new MutationObserver(checkCurrentLanguage);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    
    return () => observer.disconnect();
  }, []);

  const translatePage = () => {
    setIsLoading(true);
    
    // Find and click the Google Translate combo box
    const attemptTranslate = () => {
      const selectElement = document.querySelector('.goog-te-combo');
      
      if (selectElement) {
        const targetLang = isZulu ? 'en' : 'zu';
        selectElement.value = targetLang;
        selectElement.dispatchEvent(new Event('change', { bubbles: true }));
        
        setTimeout(() => {
          setIsZulu(!isZulu);
          setIsLoading(false);
        }, 1000);
      } else {
        // Retry after a short delay if element not found
        setTimeout(() => {
          const retrySelect = document.querySelector('.goog-te-combo');
          if (retrySelect) {
            const targetLang = isZulu ? 'en' : 'zu';
            retrySelect.value = targetLang;
            retrySelect.dispatchEvent(new Event('change', { bubbles: true }));
            setTimeout(() => {
              setIsZulu(!isZulu);
              setIsLoading(false);
            }, 1000);
          } else {
            setIsLoading(false);
            alert('Translation is loading. Please try again in a moment.');
          }
        }, 1000);
      }
    };

    attemptTranslate();
  };

  return (
    <div className="relative flex items-center">
      {/* Google Translate Element - Hidden but functional */}
      <div id="google_translate_element" style={{ position: 'absolute', top: '-9999px', left: '-9999px' }} />
      
      {/* Custom Language Toggle Button */}
      <button
        onClick={translatePage}
        disabled={isLoading}
        className={`flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 rounded-full transition-all duration-300 text-white ${
          isLoading ? 'bg-slate-500 cursor-wait' : 'bg-slate-700 hover:bg-slate-600'
        }`}
        data-testid="language-switcher"
        title={isZulu ? 'Translate to English' : 'Humusha ku-isiZulu'}
      >
        <Globe className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${isLoading ? 'animate-spin' : ''}`} />
        <span className="text-xs sm:text-sm font-medium">
          {isLoading ? '...' : (isZulu ? 'English' : 'isiZulu')}
        </span>
      </button>
    </div>
  );
};

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const location = useLocation();

  useEffect(() => {
    setOpenDropdown(null);
    setIsOpen(false);
  }, [location]);

  return (
    <>
      {/* Top Contact Bar - Enhanced Design */}
      <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white py-2.5 sm:py-3" data-testid="top-contact-bar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-between gap-2 sm:gap-4">
            {/* Left - Contact Info */}
            <div className="flex items-center gap-3 sm:gap-6">
              <a 
                href="https://wa.me/27828951916" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 sm:gap-2 hover:text-green-400 transition-all duration-300 group"
                data-testid="whatsapp-link"
              >
                <div className="bg-green-500/20 p-1 sm:p-1.5 rounded-full group-hover:bg-green-500/40 transition-all duration-300">
                  <MessageCircle className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                </div>
                <span className="text-xs sm:text-sm font-medium">WhatsApp: 082 895 1916</span>
              </a>
            </div>
            
            {/* Right - Price highlight (mobile) & Hours (desktop) */}
            <div className="flex items-center gap-2 sm:gap-4">
              {/* Language Switcher */}
              <LanguageSwitcher />
              
              {/* Hours - Hidden on mobile */}
              <div className="hidden md:flex items-center gap-2 text-slate-300">
                <Clock className="w-3.5 h-3.5 text-teal-400" />
                <span className="text-xs">Mon-Fri: 8AM-5PM | Sat: 8AM-2PM</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <nav className="bg-white/95 backdrop-blur-md sticky top-0 z-50 border-b border-slate-100 shadow-sm" data-testid="main-navbar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20 sm:h-24 lg:h-28">
            {/* Logo - Made Much Larger with Clinic Name */}
            <Link to="/" className="flex items-center gap-2 sm:gap-3" data-testid="logo-link" onClick={() => window.scrollTo(0, 0)}>
              <img 
                src={LOGO_URL} 
                alt="K&K Medical and Dental" 
                className="h-14 sm:h-20 lg:h-24 w-auto"
              />
              <div>
                <h1 className="text-base sm:text-xl lg:text-2xl font-bold text-slate-900 leading-tight notranslate">
                  K&K Medical <span className="text-teal-600">&</span> Dental
                </h1>
                <p className="hidden sm:block text-xs sm:text-sm text-slate-500">Your Trusted Healthcare Partner</p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-6">
              {navLinks.map((link) => (
                link.hasDropdown ? (
                  <div
                    key={link.path}
                    className="relative"
                    onMouseEnter={() => setOpenDropdown(link.name)}
                    onMouseLeave={() => setOpenDropdown(null)}
                  >
                    <Link
                      to={link.path}
                      onClick={() => window.scrollTo(0, 0)}
                      className={`font-medium transition-colors duration-200 flex items-center gap-1 ${
                        location.pathname === link.path
                          ? 'text-teal-600'
                          : 'text-slate-700 hover:text-teal-600'
                      }`}
                      data-testid={`nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
                    >
                      {link.name}
                      <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${openDropdown === link.name ? 'rotate-180' : ''}`} />
                    </Link>
                    {/* Dropdown Menu */}
                    <div className={`absolute top-full left-0 pt-2 transition-all duration-200 z-50 ${openDropdown === link.name ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
                      <div className="bg-white rounded-xl shadow-lg border border-slate-100 py-2 min-w-[180px]">
                        {link.dropdown.map((item) => (
                          <Link
                            key={item.path}
                            to={item.path}
                            onClick={() => window.scrollTo(0, 0)}
                            className="block px-4 py-2 text-sm text-slate-700 hover:bg-teal-50 hover:text-teal-600 transition-colors duration-150"
                            data-testid={`dropdown-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            {item.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => window.scrollTo(0, 0)}
                    className={`font-medium transition-colors duration-200 relative group ${
                      location.pathname === link.path
                        ? 'text-teal-600'
                        : 'text-slate-700 hover:text-teal-600'
                    }`}
                    data-testid={`nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
                  >
                    {link.name}
                    <span className={`absolute -bottom-1 left-0 h-0.5 bg-teal-600 transition-all duration-300 ${
                      location.pathname === link.path ? 'w-full' : 'w-0 group-hover:w-full'
                    }`} />
                  </Link>
                )
              ))}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
              data-testid="mobile-menu-btn"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isOpen && (
            <div className="lg:hidden py-4 border-t border-slate-100 animate-fade-in max-h-[calc(100vh-120px)] overflow-y-auto" data-testid="mobile-menu">
              {/* Mobile Info Bar */}
              <div className="flex flex-wrap items-center justify-center gap-3 mb-4 pb-4 border-b border-slate-100">
                <a href="tel:0399741570" className="flex items-center gap-1.5 text-slate-600 text-sm">
                  <Phone className="w-4 h-4 text-teal-600" />
                  <span>Umzinto: 039 974 1570</span>
                </a>
                <a href="tel:0393155041" className="flex items-center gap-1.5 text-slate-600 text-sm">
                  <Phone className="w-4 h-4 text-blue-500" />
                  <span>Scottburgh: 039 315 5041</span>
                </a>
                <a 
                  href="mailto:info@kkmedical.co.za" 
                  className="flex items-center gap-1.5 text-slate-600 text-sm"
                >
                  <Mail className="w-4 h-4 text-teal-600" />
                  <span>info@kkmedical.co.za</span>
                </a>
                <div className="flex items-center gap-1.5 text-slate-600 text-sm">
                  <MapPin className="w-4 h-4 text-teal-600" />
                  <span>Umzinto & Scottburgh, KZN</span>
                </div>
              </div>
              
              <div className="flex flex-col gap-2">
                {navLinks.map((link) => (
                  <div key={link.path}>
                    <Link
                      to={link.path}
                      onClick={() => { if (!link.hasDropdown) { setIsOpen(false); window.scrollTo(0, 0); } }}
                      className={`px-4 py-3 rounded-xl font-medium transition-colors duration-200 flex items-center justify-between ${
                        location.pathname === link.path
                          ? 'bg-teal-50 text-teal-600'
                          : 'text-slate-700 hover:bg-slate-50'
                      }`}
                      data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
                    >
                      {link.name}
                      {link.hasDropdown && <ChevronDown className="w-4 h-4" />}
                    </Link>
                    {link.hasDropdown && (
                      <div className="ml-4 mt-1 border-l-2 border-slate-200 pl-3">
                        {link.dropdown.map((item) => (
                          <Link
                            key={item.path}
                            to={item.path}
                            onClick={() => { setIsOpen(false); window.scrollTo(0, 0); }}
                            className="block px-3 py-2 text-sm text-slate-600 hover:text-teal-600 transition-colors"
                            data-testid={`mobile-dropdown-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            {item.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                <Button 
                  asChild
                  className="mt-2 bg-teal-600 hover:bg-teal-700 text-white rounded-full shadow-lg"
                  data-testid="mobile-book-btn"
                >
                  <a href="/booking" onClick={() => { setIsOpen(false); window.scrollTo(0, 0); }}>Book Appointment</a>
                </Button>
              </div>
              
              {/* Mobile Hours */}
              <div className="mt-4 pt-4 border-t border-slate-100 text-center">
                <p className="text-xs text-slate-500">
                  <Clock className="w-3 h-3 inline mr-1" />
                  Mon-Fri: 8AM-5PM | Sat: 8AM-2PM
                </p>
              </div>
            </div>
          )}
        </div>
      </nav>
    </>
  );
};
